import { computed, type InputSignal, type Signal } from '@angular/core';
import {
    hasSentMessage as hasSentThreadMessage,
    type QuippyChatRef,
    type QuippyConversation,
    type QuippyHistoryItem,
    type QuippyMessage,
    type QuippyScope,
    type QuippySession,
} from '../runtime';

export function quippyScopeRowIdentity(
    ref: QuippyChatRef,
    scope: QuippyScope
): string {
    return JSON.stringify([
        ref.chatId,
        scope.kind,
        scope.kind === 'building' ? scope.buildingId : null,
    ]);
}

export interface QuippyAssistantUiState {
    conversation: Signal<QuippyConversation | null>;
    currentChatId: Signal<string | null>;
    messages: Signal<readonly QuippyMessage[]>;
    draft: Signal<string>;
    sidebarThreads: Signal<readonly QuippyHistoryItem[]>;
    showSuggestions: Signal<boolean>;
    hasSentMessage: Signal<boolean>;
    isLoading: Signal<boolean>;
    chatSize: Signal<'off' | 'open'>;
}

export function createQuippyAssistantUiState(options: {
    session: QuippySession;
    chatRef: InputSignal<QuippyChatRef>;
    isOpen: InputSignal<boolean>;
}): QuippyAssistantUiState {
    const conversation = computed(() =>
        options.session.read(options.chatRef())
    );
    const currentChatId = computed(() => conversation()?.thread.id ?? null);
    const messages = computed(() => conversation()?.thread.messages ?? []);
    const draft = computed(() => conversation()?.draft ?? '');
    const sidebarThreads = computed(() => options.session.history());
    const hasSentMessage = computed(() => {
        const thread = conversation()?.thread;
        return thread !== undefined && hasSentThreadMessage(thread);
    });
    const showSuggestions = computed(() => !hasSentMessage());
    const isLoading = computed(() =>
        messages().some(
            (message) =>
                message.role === 'assistant' && message.status === 'pending'
        )
    );
    const chatSize = computed<'off' | 'open'>(() =>
        options.isOpen() ? 'open' : 'off'
    );

    return {
        conversation,
        currentChatId,
        messages,
        draft,
        sidebarThreads,
        showSuggestions,
        hasSentMessage,
        isLoading,
        chatSize,
    };
}
